import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

import config from '../config';
const API_BASE_URL = config.API_BASE_URL;

const JobTitleDropdown = ({
  value,
  onChange,
  onKeyPress,
  isDarkMode,
  placeholder = "e.g., React Developer, Data Scientist"
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [initialTitles, setInitialTitles] = useState([]); // Cache for initial titles
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync with external value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch initial titles on mount (for showing on focus)
  useEffect(() => {
    const fetchInitialTitles = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/job-titles/`, {
          params: {
            skip: 0,
            limit: 100
          }
        });
        setInitialTitles(response.data || []);
      } catch (error) {
        console.error('Error fetching initial job titles:', error);
      }
    };
    fetchInitialTitles();
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    // If no query, show initial titles
    if (!query || query.trim() === '') {
      setSuggestions(initialTitles);
      setIsOpen(initialTitles.length > 0);
      setHighlightedIndex(-1);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/job-titles/`, {
        params: {
          q: query,
          skip: 0,
          limit: 10
        }
      });
      const titles = response.data || [];
      setSuggestions(titles);
      setIsOpen(titles.length > 0);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('Error fetching job titles:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [initialTitles]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleFocus = () => {
    // Show initial titles or search results on focus
    if (inputValue.trim() === '') {
      setSuggestions(initialTitles);
      setIsOpen(initialTitles.length > 0);
    } else {
      fetchSuggestions(inputValue);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    const title = typeof suggestion === 'string' ? suggestion : (suggestion.keyword || suggestion.title);
    setInputValue(title);
    onChange(title);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'Enter' && onKeyPress) {
        onKeyPress(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        } else if (onKeyPress) {
          setIsOpen(false);
          onKeyPress(e);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const inputClasses = `w-full pl-12 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all
    ${isDarkMode
      ? 'bg-[#1C1C1C] border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
      : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
    }`;

  const dropdownClasses = `absolute z-50 w-full mt-1 rounded-lg border shadow-lg max-h-60 overflow-y-auto
    ${isDarkMode
      ? 'bg-[#1C1C1C] border-gray-600'
      : 'bg-white border-gray-300'
    }`;

  const itemClasses = (isHighlighted) => `px-4 py-3 cursor-pointer transition-colors
    ${isHighlighted
      ? isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
      : isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
    }
    ${isDarkMode ? 'text-white' : 'text-gray-900'}`;

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
        {loading ? (
          <FaSpinner className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} h-5 w-5 animate-spin`} />
        ) : (
          <FaSearch className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} h-5 w-5`} />
        )}
      </div>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={inputClasses}
        aria-required="true"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        autoComplete="off"
      />
      <AnimatePresence>
        {isOpen && suggestions.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className={dropdownClasses}
            role="listbox"
          >
            {suggestions.map((suggestion, index) => {
              const title = typeof suggestion === 'string' ? suggestion : (suggestion.keyword || suggestion.title);
              return (
                <li
                  key={index}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={itemClasses(index === highlightedIndex)}
                  role="option"
                  aria-selected={index === highlightedIndex}
                >
                  {title}
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JobTitleDropdown;